/* M5 dashboard chart primitives — pure SVG/markup, no chart lib, matching the
   scoped CSS (.donut-svg/.donut-wrap/.legend, .vbars, .minibar, .barrow, .age-legend).
   Ports PS.donut / the vertical raised-vs-resolved bars / mini horizontal bars. */
import React from 'react'
import PropTypes from 'prop-types'
import {inr} from '../dummyData'

/* ── SVG donut + legend (PS.donut equivalent) ── */
export function Donut({segments, unit = 'invoices'}) {
  const segs = (segments || []).filter((s) => s.value > 0)
  const total = (segments || []).reduce((s, x) => s + x.value, 0)
  const R = 66
  const C = 84
  const STROKE = 22
  const circ = 2 * Math.PI * R
  let offset = 0
  return (
    <div className='donut-wrap'>
      <div className='donut-svgwrap'>
        <svg className='donut-svg' width='168' height='168' viewBox='0 0 168 168'>
          <circle cx={C} cy={C} r={R} fill='none' stroke='#EEF2F6' strokeWidth={STROKE} />
          {total > 0
            ? segs.map((s, i) => {
                const frac = s.value / total
                const dash = frac * circ
                const el = (
                  <circle
                    key={i}
                    className='donut-seg'
                    cx={C}
                    cy={C}
                    r={R}
                    fill='none'
                    stroke={s.color}
                    strokeWidth={STROKE}
                    strokeDasharray={`${dash} ${circ - dash}`}
                    strokeDashoffset={-offset}
                    transform={`rotate(-90 ${C} ${C})`}
                  >
                    <title>{`${s.label}: ${s.value}`}</title>
                  </circle>
                )
                offset += dash
                return el
              })
            : null}
          <text
            className='donut-total'
            x={C}
            y={C - 2}
            textAnchor='middle'
            dominantBaseline='middle'
          >
            {total}
          </text>
          <text className='donut-unit' x={C} y={C + 18} textAnchor='middle'>
            {unit}
          </text>
        </svg>
      </div>
      <div className='legend'>
        {(segments || []).map((s, i) => (
          <div className='li' key={i}>
            <span className='sw' style={{background: s.color}} />
            {s.label}
            <span className='v'>{s.value}</span>
            <span className='a'>{total ? Math.round((s.value / total) * 100) + '%' : '0%'}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
Donut.propTypes = {segments: PropTypes.array, unit: PropTypes.string}

/* ── stacked horizontal mini-bar (PS.miniBar) ── */
export function MiniBar({segments, height = 9}) {
  const total = (segments || []).reduce((s, x) => s + x.v, 0) || 1
  return (
    <div className='minibar' style={{height}}>
      {(segments || []).map((s, i) =>
        s.v ? (
          <span
            key={i}
            title={`${s.label}: ${inr(s.v)}`}
            style={{width: ((s.v / total) * 100).toFixed(1) + '%', background: s.color}}
          />
        ) : null
      )}
    </div>
  )
}
MiniBar.propTypes = {segments: PropTypes.array, height: PropTypes.number}

/* ── vertical raised-vs-resolved bars (animates height on mount) ── */
export function TrendBars({months, trend}) {
  const [grown, setGrown] = React.useState(false)
  React.useEffect(() => {
    const id = requestAnimationFrame(() => setGrown(true))
    return () => cancelAnimationFrame(id)
  }, [])
  const max = Math.max(
    1,
    ...months.map((m) =>
      Math.max(trend[m.k] ? trend[m.k].raised : 0, trend[m.k] ? trend[m.k].resolved : 0)
    )
  )
  return (
    <>
      <div className='vbars'>
        {months.map((m) => {
          const t = trend[m.k] || {raised: 0, resolved: 0}
          return (
            <div className='vbcol' key={m.k}>
              <div style={{fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap'}}>
                <span style={{color: '#B45309'}}>{t.raised}</span>
                <span className='muted'> / </span>
                <span style={{color: '#0F766E'}}>{t.resolved}</span>
              </div>
              <div className='stack'>
                <span
                  className='b'
                  title={`Raised: ${t.raised}`}
                  style={{height: grown ? (t.raised / max) * 100 + '%' : 0, background: '#B45309'}}
                />
                <span
                  className='b'
                  title={`Resolved: ${t.resolved}`}
                  style={{
                    height: grown ? (t.resolved / max) * 100 + '%' : 0,
                    background: '#0F766E',
                  }}
                />
              </div>
              <div className='mlbl'>{m.label.replace(/^\d+\D\d+ /, '').replace(/ 20\d\d/, '')}</div>
            </div>
          )
        })}
      </div>
      <div className='age-legend' style={{justifyContent: 'center'}}>
        <div className='li'>
          <span className='sw' style={{background: '#B45309'}} />
          Raised
        </div>
        <div className='li'>
          <span className='sw' style={{background: '#0F766E'}} />
          Resolved
        </div>
      </div>
    </>
  )
}
TrendBars.propTypes = {months: PropTypes.array, trend: PropTypes.object}
